import React from 'react';
import { Link } from 'react-router-dom';
import './TermsOfService.css';

const TermsOfService = () => {
  return (
    <div className="terms-of-service-page">
      <div className="terms-container">
        <div className="terms-header">
          <Link to="/" className="back-link">← Back to Home</Link>
          <h1 className="terms-title">Terms of Service</h1>
          <p className="terms-last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="terms-content">
          <p className="terms-intro">
            Welcome to LearnFi Business (BizFi). These Terms of Service ("Terms") govern your access to and use of all services, products, and websites operated by LearnFi (Pvt) Ltd (PV 00325492) ("LearnFi", "we", "us", "our").
          </p>

          <p className="terms-intro">
            By using our website or services, you agree to these Terms. If you do not agree, please stop using our services.
          </p>

          <section className="terms-section">
            <h2 className="terms-section-title">1. DEFINITIONS</h2>
            <p className="terms-text">
              <strong>"Services"</strong> – Software development, digital solutions, consulting, web apps, systems, and any service provided by LearnFi.
            </p>
            <p className="terms-text">
              <strong>"Client", "User", "You"</strong> – Anyone who accesses our website, requests a project, or uses our products.
            </p>
            <p className="terms-text">
              <strong>"Agreement"</strong> – These Terms, including any project proposal or contract signed between the parties.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">2. ACCEPTANCE OF TERMS</h2>
            <p className="terms-text">
              By accessing or using LearnFi services, you confirm that you:
            </p>
            <ul className="terms-list">
              <li>Are at least 18 years old</li>
              <li>Have the authority to enter into a legally binding agreement</li>
              <li>Agree to follow all policies published on our website</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">3. SERVICES WE PROVIDE</h2>
            <p className="terms-text">
              LearnFi Business offers:
            </p>
            <ul className="terms-list">
              <li>Custom software development</li>
              <li>Web & mobile application development</li>
              <li>Cloud solutions & integrations</li>
              <li>Business automation systems</li>
              <li>UI/UX design</li>
              <li>IT consulting & maintenance services</li>
            </ul>
            <p className="terms-text">
              Specific project details, timelines, and payments will be defined in individual proposals or contracts.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">4. PROJECT AGREEMENTS</h2>
            <p className="terms-text">
              Before starting any project, LearnFi will provide:
            </p>
            <ul className="terms-list">
              <li>A quotation or proposal</li>
              <li>A scope of work</li>
              <li>Delivery timelines</li>
              <li>Payment terms</li>
            </ul>
            <p className="terms-text">
              A project begins only after:
            </p>
            <ul className="terms-list">
              <li>The client approves the proposal</li>
              <li>Initial payment (if required) is made</li>
            </ul>
            <p className="terms-text">
              Any changes requested after approval may require additional charges or extended deadlines.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">5. PAYMENTS</h2>
            <ul className="terms-list">
              <li>All payments must be made according to the proposal or invoice.</li>
              <li>LearnFi may pause or stop work if payments are delayed.</li>
              <li>Completed work or delivered software will not be transferred until full payment is completed.</li>
              <li>Payments are generally non-refundable unless otherwise stated in a separate Refund Policy.</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">6. CLIENT RESPONSIBILITIES</h2>
            <p className="terms-text">
              Clients must:
            </p>
            <ul className="terms-list">
              <li>Provide accurate information required to complete the project</li>
              <li>Respond to communications on time</li>
              <li>Supply necessary content, access, or materials</li>
              <li>Use the delivered software responsibly and lawfully</li>
            </ul>
            <p className="terms-text">
              Delays caused by the client may impact delivery timelines.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">7. INTELLECTUAL PROPERTY</h2>
            <p className="terms-text">
              Unless otherwise agreed:
            </p>
            <ul className="terms-list">
              <li>LearnFi retains rights to its tools, frameworks, and code libraries.</li>
              <li>Final deliverables (software, design, content) become the client's property after full payment.</li>
              <li>Clients may not resell or redistribute LearnFi's proprietary templates, systems, or development methods without written permission.</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">8. SOFTWARE WARRANTY & SUPPORT</h2>
            <p className="terms-text">
              LearnFi provides:
            </p>
            <ul className="terms-list">
              <li>A limited warranty period for bug fixes (usually 14–30 days depending on the project).</li>
              <li>Support or maintenance contracts if separately purchased.</li>
            </ul>
            <p className="terms-text">
              Warranty does not cover:
            </p>
            <ul className="terms-list">
              <li>Third-party issues</li>
              <li>Hosting or server problems</li>
              <li>Misuse or unauthorized modifications</li>
              <li>New feature requests</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">9. THIRD-PARTY SERVICES</h2>
            <p className="terms-text">
              Some services may depend on:
            </p>
            <ul className="terms-list">
              <li>Hosting providers</li>
              <li>Cloud services</li>
              <li>APIs</li>
              <li>Payment gateways</li>
              <li>External software</li>
            </ul>
            <p className="terms-text">
              LearnFi is not responsible for outages, limitations, or failures caused by third parties.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">10. LIMITATION OF LIABILITY</h2>
            <p className="terms-text">
              To the maximum extent allowed by law:
            </p>
            <ul className="terms-list">
              <li>LearnFi is not responsible for indirect, incidental, or consequential damages.</li>
              <li>LearnFi's total liability will not exceed the amount paid by the client for the specific project.</li>
              <li>Use of our software and services is at your own risk.</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">11. TERMINATION</h2>
            <p className="terms-text">
              LearnFi may suspend or terminate service if:
            </p>
            <ul className="terms-list">
              <li>Payments are overdue</li>
              <li>The client violates these Terms</li>
              <li>The client engages in fraudulent or abusive behavior</li>
            </ul>
            <p className="terms-text">
              Clients may terminate the project but are responsible for paying for completed work up to the termination date.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">12. PRIVACY</h2>
            <p className="terms-text">
              All personal information is handled according to our Privacy Policy. By using our services, you consent to our data practices.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">13. GOVERNING LAW</h2>
            <p className="terms-text">
              These Terms are governed by the laws of Sri Lanka. Any disputes must be resolved in Sri Lankan courts.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">14. CHANGES TO TERMS</h2>
            <p className="terms-text">
              LearnFi may update these Terms at any time. Changes will take effect upon posting on our website.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="terms-section-title">15. CONTACT US</h2>
            <p className="terms-text">
              For questions regarding these Terms, contact:
            </p>
            <div className="terms-contact-info">
              <p className="terms-text"><strong>LEARNFI (PVT) LTD</strong></p>
              <p className="terms-text">Colombo, Sri Lanka</p>
              <p className="terms-text">Email: support@learnfi.lk</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;

